import React from 'react';
import { TextInput, Textarea } from '@parsina/framework';
import { C } from '@parsina/framework/tokens';
import { useMyPageStrings } from '../myPageStrings';
import { useDraft } from '../useDraft';
import { LIMITS, heroDraft, saveStatus, usesCover, overLimit } from '../MyPage.shared';
import { ErrorLine, ImageSlot, SaveRow, useFieldStyle } from '../parts/common';
import HeroExtras from './HeroExtras';

// «پروفایل حرفه‌ای» — the hero: the name the page carries, one line under it,
// the about text (sanitised markdown), the portrait and — for a template that
// shows one — the cover. The page's own copy: editing it never touches the
// doctor's clinical identity, and vice versa (design §2, §8.9).
export default function HeroEditor({ page, section, actions, cards, readOnly }) {
  const s = useMyPageStrings();
  const fieldStyle = useFieldStyle();
  const { draft, setDraft, dirty, adopt, reset } = useDraft(heroDraft(page));
  const card = cards.hero || {};
  const set = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));
  const invalid = !draft.displayName.trim()
    || overLimit(draft.displayName, LIMITS.display_name_max)
    || overLimit(draft.headline, LIMITS.headline_max)
    || overLimit(draft.aboutMd, LIMITS.about_md_max);

  const save = async () => {
    const res = await actions.updatePage('hero', {
      displayName: draft.displayName.trim(), headline: draft.headline.trim(), aboutMd: draft.aboutMd,
    });
    if (res.ok) adopt(heroDraft(res.page));
  };

  const upload = (slot) => async (image) => {
    const res = await actions.uploadImage(`img-${slot}`, { slot, image, alt: draft.displayName || page.displayName });
    return res.ok;
  };
  const remove = (media) => async () => { await actions.removeImage(`img-${media.slot}`, media.id); };
  const coverShown = usesCover(page.templateId);

  return (
    <div style={{ display: 'grid', gap: '1rem' }} data-testid="my-page-hero">
      <TextInput
        label={s('myPage.hero.displayName')}
        value={draft.displayName}
        onChange={set('displayName')}
        maxLength={LIMITS.display_name_max}
        disabled={readOnly}
        error={!draft.displayName.trim() && dirty ? s('myPage.hero.displayNameRequired') : undefined}
        inputStyle={fieldStyle}
        data-testid="my-page-hero-name"
      />
      <TextInput
        label={s('myPage.hero.headline')}
        placeholder={s('myPage.hero.headlinePlaceholder')}
        value={draft.headline}
        onChange={set('headline')}
        maxLength={LIMITS.headline_max}
        disabled={readOnly}
        helperText={s('myPage.hero.headlineHint')}
        inputStyle={fieldStyle}
        data-testid="my-page-hero-headline"
      />
      <Textarea
        label={s('myPage.hero.about')}
        value={draft.aboutMd}
        onChange={set('aboutMd')}
        rows={8}
        maxLength={LIMITS.about_md_max}
        showCount
        disabled={readOnly}
        helperText={s('myPage.markdownHint')}
        inputStyle={fieldStyle}
        dir="auto"
        data-testid="my-page-hero-about"
      />
      <SaveRow
        status={saveStatus({ dirty, saving: card.saving, error: card.error, savedAt: card.savedAt })}
        error={card.error}
        onSave={save}
        onReset={reset}
        disabled={readOnly || invalid}
        testId="my-page-hero"
      />

      <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: '1rem' }}>
        <HeroExtras page={page} section={section} actions={actions} cards={cards} readOnly={readOnly} />
      </div>

      <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: '1rem', display: 'grid', gap: '1.25rem' }}>
        <ImageSlot
          slot="portrait"
          label={s('myPage.hero.portrait')}
          hint={s('myPage.hero.portraitHint')}
          media={page.portrait}
          busy={!!cards['img-portrait']?.saving}
          disabled={readOnly}
          onUpload={upload('portrait')}
          onRemove={page.portrait ? remove(page.portrait) : undefined}
          testId="my-page-hero-portrait"
        />
        <ImageSlot
          slot="cover"
          label={s('myPage.hero.cover')}
          hint={coverShown ? s('myPage.hero.coverHint') : s('myPage.hero.coverUnused')}
          media={page.cover}
          busy={!!cards['img-cover']?.saving}
          disabled={readOnly}
          onUpload={upload('cover')}
          onRemove={page.cover ? remove(page.cover) : undefined}
          testId="my-page-hero-cover"
        />
        <ImageErrors cards={cards} keys={['img-portrait', 'img-cover']} />
      </div>
    </div>
  );
}

/** Image failures shown under the slots that raised them. */
export function ImageErrors({ cards, keys }) {
  return keys.filter((k) => cards[k]?.error).map((k) => <ErrorLine key={k} error={cards[k].error} testId={`my-page-${k}-error`} />);
}
